import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Store, MapPin, Phone, Globe, Clock, Tag, Users,
  Package, Percent, Play, ExternalLink, BadgeCheck, ShoppingCart,
  Plus, Minus, X, Calendar, ChevronRight, Check, Trash2,
  Camera, Star, ThumbsUp, ImageIcon, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'fruits_legumes', label: 'Fruits & Légumes', icon: '🥦' },
  { value: 'viandes', label: 'Viandes', icon: '🥩' },
  { value: 'poissons', label: 'Poissons', icon: '🐟' },
  { value: 'produits_laitiers', label: 'Produits laitiers', icon: '🧀' },
  { value: 'epicerie', label: 'Épicerie', icon: '🍞' },
  { value: 'boissons', label: 'Boissons', icon: '🧋' },
  { value: 'surgeles', label: 'Surgelés', icon: '❄️' },
  { value: 'autre', label: 'Autre', icon: '📦' }
];

const BUSINESS_TYPES = {
  'restaurant': { label: 'Restaurant', icon: '🍽️' },
  'epicerie': { label: 'Épicerie', icon: '🛒' },
  'supermarche': { label: 'Supermarché', icon: '🏪' },
  'producteur': { label: 'Producteur local', icon: '🌾' },
  'marche': { label: 'Marché', icon: '🥬' },
  'boucherie': { label: 'Boucherie', icon: '🥩' },
  'poissonnerie': { label: 'Poissonnerie', icon: '🐟' },
  'boulangerie': { label: 'Boulangerie', icon: '🥖' },
  'autre': { label: 'Commerce', icon: '🏬' }
};

const DAYS = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];

export default function StorePublicPage() {
  const { storeId } = useParams();
  const navigate = useNavigate();
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showPromoOnly, setShowPromoOnly] = useState(false);
  
  // Cart state
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [showCart, setShowCart] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  
  // Restaurant state
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({ rating_cuisine: 5, rating_service: 5, rating_ambiance: 5, rating_value: 5, comment: '' });
  
  // Gamification state
  const [gallery, setGallery] = useState(null);
  const [showPhotoUpload, setShowPhotoUpload] = useState(false);
  const [photoTargetItem, setPhotoTargetItem] = useState(null);
  const [photoCaption, setPhotoCaption] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [showDishRating, setShowDishRating] = useState(false);
  const [ratingTargetItem, setRatingTargetItem] = useState(null);
  const [dishRatingForm, setDishRatingForm] = useState({ taste: 5, presentation: 5, value_for_money: 5, comment: '' });
  
  // Checkout state
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [checkoutData, setCheckoutData] = useState({
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchStore();
    fetchCart();
  }, [storeId]);

  const fetchStore = async () => {
    try {
      const [storeRes, productsRes] = await Promise.all([
        fetch(`${API_URL}/api/business/${storeId}/public`),
        fetch(`${API_URL}/api/business/${storeId}/catalog/public`)
      ]);

      if (!storeRes.ok) throw new Error('Boutique non trouvée');

      setStore(await storeRes.json());
      if (productsRes.ok) setProducts(await productsRes.json());
      
      // Fetch reviews and menu for restaurants
      const storeData = store || (await storeRes.json ? null : null);
      try {
        const [reviewsRes, menuRes] = await Promise.all([
          fetch(`${API_URL}/api/business/${storeId}/reviews`),
          fetch(`${API_URL}/api/business/${storeId}/menu`)
        ]);
        if (reviewsRes.ok) {
          const rd = await reviewsRes.json();
          setReviews(rd.reviews || []);
          setReviewStats(rd.stats);
        }
        if (menuRes.ok) {
          const md = await menuRes.json();
          setMenuItems(md.items || []);
        }
        // Fetch gallery
        const galleryRes = await fetch(`${API_URL}/api/restaurants/${storeId}/gallery`);
        if (galleryRes.ok) setGallery(await galleryRes.json());
      } catch (e) { /* non-critical */ }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !photoTargetItem) return;
    const token = localStorage.getItem('token');
    if (!token) { toast.error('Connectez-vous pour poster une photo'); navigate('/login'); return; }
    
    setUploadingPhoto(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result;
        const res = await fetch(`${API_URL}/api/restaurants/${storeId}/dishes/${photoTargetItem.id}/photo`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ menu_item_id: photoTargetItem.id, photo_base64: base64, caption: photoCaption })
        });
        if (!res.ok) { const d = await res.json(); throw new Error(d.detail); }
        const data = await res.json();
        toast.success(`Photo envoyée ! +${data.points_earned} points. En attente de validation.`);
        setShowPhotoUpload(false);
        setPhotoCaption('');
        fetchStore();
      };
      reader.readAsDataURL(file);
    } catch (err) { toast.error(err.message); }
    setUploadingPhoto(false);
  };

  const submitDishRating = async () => {
    if (!ratingTargetItem) return;
    const token = localStorage.getItem('token');
    if (!token) { toast.error('Connectez-vous pour noter un plat'); navigate('/login'); return; }
    try {
      const res = await fetch(`${API_URL}/api/restaurants/${storeId}/dishes/${ratingTargetItem.id}/rate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(dishRatingForm)
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail); }
      const data = await res.json();
      toast.success(`Note publiée ! +${data.points_earned} points`);
      setShowDishRating(false);
      setDishRatingForm({ taste: 5, presentation: 5, value_for_money: 5, comment: '' });
      fetchStore();
    } catch (err) { toast.error(err.message); }
  };

  const StarRating = ({ value, onChange, label }) => (
    <div className="flex items-center justify-between">
      <span className="text-sm text-muted-foreground">{label}</span>
      <div className="flex gap-0.5">
        {[1,2,3,4,5].map(n => (
          <button key={n} onClick={() => onChange(n)} className="p-0.5">
            <Star className={`w-5 h-5 transition-colors ${n <= value ? 'fill-amber-400 text-amber-400' : 'text-gray-200'}`} />
          </button>
        ))}
      </div>
    </div>
  );


  const submitReview = async () => {
    const token = localStorage.getItem('token');
    if (!token) { toast.error('Connectez-vous pour laisser un avis'); navigate('/login'); return; }
    try {
      const res = await fetch(`${API_URL}/api/business/${storeId}/reviews`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(reviewForm)
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail); }
      toast.success('Avis publié ! +3 points');
      setShowReviewForm(false);
      setReviewForm({ rating_cuisine: 5, rating_service: 5, rating_ambiance: 5, rating_value: 5, comment: '' });
      // Refresh reviews
      const reviewsRes = await fetch(`${API_URL}/api/business/${storeId}/reviews`);
      if (reviewsRes.ok) { const rd = await reviewsRes.json(); setReviews(rd.reviews || []); setReviewStats(rd.stats); }
    } catch (err) { toast.error(err.message); }
  };


  const fetchCart = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const res = await fetch(`${API_URL}/api/cart/${storeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setCart(await res.json());
      }
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  };

  const addToCart = async (productId) => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Connectez-vous pour commander');
      navigate('/login');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/cart/${storeId}/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ product_id: productId, quantity: 1 })
      });

      if (res.ok) {
        const data = await res.json();
        setCart(data);
        toast.success('Ajouté au panier');
      } else {
        throw new Error('Erreur');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const updateCartItem = async (productId, quantity) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const res = await fetch(`${API_URL}/api/cart/${storeId}/update`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ product_id: productId, quantity })
      });

      if (res.ok) {
        setCart(await res.json());
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const clearCart = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      await fetch(`${API_URL}/api/cart/${storeId}/clear`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      setCart({ items: [], total: 0 });
      setShowCart(false);
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const fetchAvailableSlots = async (date) => {
    try {
      const res = await fetch(`${API_URL}/api/business/${storeId}/pickup-slots/available?date=${date}`);
      if (res.ok) {
        setAvailableSlots(await res.json());
      }
    } catch (error) {
      console.error('Error fetching slots:', error);
    }
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setSelectedSlot(null);
    if (date) {
      fetchAvailableSlots(date);
    }
  };

  const submitOrder = async () => {
    if (!selectedSlot || !checkoutData.customer_name || !checkoutData.customer_phone) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Connectez-vous pour commander');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          business_id: storeId,
          pickup_date: selectedDate,
          pickup_slot_id: selectedSlot.id,
          ...checkoutData
        })
      });

      if (res.ok) {
        const order = await res.json();
        toast.success(`Commande #${order.order_number} confirmée !`);
        setShowCheckout(false);
        setShowCart(false);
        setCart({ items: [], total: 0 });
        // Reset checkout state
        setSelectedDate('');
        setSelectedSlot(null);
        setCheckoutData({ customer_name: '', customer_phone: '', customer_email: '', notes: '' });
      } else {
        const error = await res.json();
        throw new Error(error.detail || 'Erreur');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Get available dates (next 7 days)
  const getAvailableDates = () => {
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date();
      date.setDate(date.getDate() + i);
      dates.push({
        value: date.toISOString().split('T')[0],
        label: i === 0 ? 'Aujourd\'hui' : i === 1 ? 'Demain' : `${DAYS[date.getDay()]} ${date.getDate()}/${date.getMonth() + 1}`
      });
    }
    return dates;
  };

  const filteredProducts = products.filter(p => {
    const matchesCategory = !selectedCategory || p.category === selectedCategory;
    const matchesPromo = !showPromoOnly || p.is_promotion;
    return matchesCategory && matchesPromo;
  });

  const getYoutubeEmbedUrl = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\\w-]+)/);
    return match ? `https://www.youtube.com/embed/${match[1]}` : null;
  };

  const getItemInCart = (productId) => {
    return cart.items.find(item => item.product_id === productId);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!store) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Store className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-charcoal mb-2">Boutique non trouvée</h1>
          <Button asChild>
            <Link to="/discover">Retour à la découverte</Link>
          </Button>
        </div>
      </div>
    );
  }

  const businessType = BUSINESS_TYPES[store.business_type] || BUSINESS_TYPES.autre;

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Cover Image */}
      <div className="h-48 md:h-64 bg-gradient-to-r from-amber-400 to-orange-500 relative">
        {store.cover_image && (
          <img src={store.cover_image} alt="Cover" className="w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
        
        <Button 
          variant="ghost" 
          size="icon" 
          className="absolute top-4 left-4 bg-white/20 backdrop-blur-sm hover:bg-white/30"
          asChild
        >
          <Link to="/discover">
            <ArrowLeft className="w-5 h-5 text-white" />
          </Link>
        </Button>
      </div>

      {/* Store Header */}
      <div className="max-w-6xl mx-auto px-4 -mt-16 relative z-10">
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Logo */}
            <div className="w-24 h-24 md:w-32 md:h-32 rounded-xl bg-gray-100 border-4 border-white shadow-lg overflow-hidden flex-shrink-0 -mt-16 md:-mt-20">
              {store.logo ? (
                <img src={store.logo} alt={store.business_name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-4xl">
                  {businessType.icon}
                </div>
              )}
            </div>

            {/* Info */}
            <div className="flex-1">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h1 className="text-2xl font-bold text-charcoal">{store.business_name}</h1>
                    {store.is_verified && (
                      <BadgeCheck className="w-6 h-6 text-primary" />
                    )}
                  </div>
                  <p className="text-muted-foreground flex items-center gap-2">
                    <span>{businessType.icon}</span>
                    {businessType.label}
                  </p>
                </div>
                <div className="flex gap-2">
                  <span className="bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-medium">
                    {store.products_count} produits
                  </span>
                  {store.promotions_count > 0 && (
                    <span className="bg-red-100 text-red-600 px-3 py-1 rounded-full text-sm font-medium">
                      {store.promotions_count} promos
                    </span>
                  )}
                </div>
              </div>

              {store.description && (
                <p className="text-sm text-muted-foreground mt-3">{store.description}</p>
              )}

              <div className="flex flex-wrap gap-4 mt-4 text-sm">
                <span className="flex items-center gap-1 text-muted-foreground">
                  <MapPin className="w-4 h-4" />
                  {store.address}, {store.postal_code} {store.city}
                </span>
                {store.phone && (
                  <a href={`tel:${store.phone}`} className="flex items-center gap-1 text-primary hover:underline">
                    <Phone className="w-4 h-4" />
                    {store.phone}
                  </a>
                )}
              </div>

              {/* Producer subcategories badges */}
              {store.producer_subcategories && store.producer_subcategories.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3" data-testid="producer-badges">
                  {store.producer_subcategories.map(sub => {
                    const subDefs = {
                      domaine_viticole: { label: "Domaine viticole", emoji: "🍇" },
                      ferme: { label: "Ferme", emoji: "🐄" },
                      maraicher: { label: "Maraîcher", emoji: "🥬" },
                      verger: { label: "Verger", emoji: "🍎" },
                      apiculteur: { label: "Apiculteur", emoji: "🍯" },
                      fromagerie_artisanale: { label: "Fromagerie artisanale", emoji: "🧀" },
                      elevage: { label: "Élevage", emoji: "🐔" },
                      brasserie: { label: "Brasserie artisanale", emoji: "🍺" },
                      conserverie: { label: "Conserverie", emoji: "🫙" },
                      oleiculteur: { label: "Oléiculteur", emoji: "🫒" },
                      trufficulture: { label: "Trufficulture", emoji: "🍄" }
                    };
                    const def = subDefs[sub] || { label: sub, emoji: "🌾" };
                    return (
                      <span key={sub} className="bg-lime-100 text-lime-700 px-2 py-0.5 rounded-full text-xs font-medium" data-testid={`producer-badge-${sub}`}>
                        {def.emoji} {def.label}
                      </span>
                    );
                  })}
                </div>
              )}

              {/* Restaurant accessibility & features badges */}
              {store.business_type === 'restaurant' && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {store.cuisine_type && (
                    <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full text-xs font-medium">
                      {store.cuisine_type}
                    </span>
                  )}
                  {store.price_range && (
                    <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-xs font-medium">
                      {store.price_range}
                    </span>
                  )}
                  {store.pmr_accessible && (
                    <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs font-medium" data-testid="badge-pmr">
                      ♿ PMR accessible
                    </span>
                  )}
                  {store.animals_allowed && (
                    <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full text-xs font-medium" data-testid="badge-animals">
                      🐾 Animaux bienvenus
                    </span>
                  )}
                  {store.adapted_lighting && (
                    <span className="bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full text-xs font-medium">
                      💡 Luminosité adaptée
                    </span>
                  )}
                  {store.ambiance && (
                    <span className="bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full text-xs font-medium">
                      {store.ambiance === 'calme' ? '🤫 Calme' : store.ambiance === 'anime' ? '🎉 Animé' : '🎵 Modéré'}
                    </span>
                  )}
                  {store.has_click_collect && (
                    <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full text-xs font-medium" data-testid="badge-click-collect">
                      📦 Click & Collect
                    </span>
                  )}
                  {store.has_dine_in && (
                    <span className="bg-rose-100 text-rose-700 px-2 py-0.5 rounded-full text-xs font-medium">
                      🍽️ Sur place
                    </span>
                  )}
                  {reviewStats && (
                    <span className="bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full text-xs font-medium" data-testid="badge-rating">
                      ⭐ {reviewStats.avg_overall}/5 ({reviewStats.count} avis)
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <Tabs defaultValue={store.business_type === 'restaurant' ? 'menu' : 'catalog'} className="w-full">
          <TabsList className="mb-6">
            {store.business_type === 'restaurant' && (
              <TabsTrigger value="menu" className="flex items-center gap-2" data-testid="tab-menu">
                📋 Menu
              </TabsTrigger>
            )}
            <TabsTrigger value="catalog" className="flex items-center gap-2">
              <Package className="w-4 h-4" /> {store.business_type === 'restaurant' ? 'A emporter' : 'Commander'}
            </TabsTrigger>
            {store.business_type === 'restaurant' && (
              <TabsTrigger value="reviews" className="flex items-center gap-2" data-testid="tab-reviews">
                <Star className="w-4 h-4" /> Avis {reviewStats ? `(${reviewStats.count})` : ''}
              </TabsTrigger>
            )}
            {store.business_type === 'restaurant' && gallery && gallery.total_photos > 0 && (
              <TabsTrigger value="gallery" className="flex items-center gap-2" data-testid="tab-gallery">
                <ImageIcon className="w-4 h-4" /> Galerie ({gallery.total_photos})
              </TabsTrigger>
            )}
            <TabsTrigger value="about" className="flex items-center gap-2">
              <Store className="w-4 h-4" /> À propos
            </TabsTrigger>
          </TabsList>

          {/* Restaurant Menu Tab */}
          {store.business_type === 'restaurant' && (
            <TabsContent value="menu">
              {menuItems.length === 0 ? (
                <div className="bg-white rounded-xl p-12 text-center">
                  <p className="text-4xl mb-3">🍽️</p>
                  <p className="text-muted-foreground">Le menu n'a pas encore été publié</p>
                </div>
              ) : (
                <div className="space-y-8">
                  {['entree', 'plat', 'dessert', 'boisson', 'menu'].map(cat => {
                    const catItems = menuItems.filter(i => i.category === cat);
                    if (catItems.length === 0) return null;
                    const catLabels = { entree: '🥗 Entrées', plat: '🍽️ Plats', dessert: '🍰 Desserts', boisson: '🥤 Boissons', menu: '📋 Menus/Formules' };
                    return (
                      <div key={cat}>
                        <h3 className="text-lg font-semibold mb-3">{catLabels[cat]}</h3>
                        <div className="space-y-2">
                          {catItems.map(item => (
                            <div key={item.id} className="bg-white rounded-xl p-4" data-testid={`pub-menu-item-${item.id}`}>
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <p className="font-medium">{item.name}</p>
                                    {item.avg_overall > 0 && (
                                      <span className="flex items-center gap-0.5 text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full">
                                        <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                                        {item.avg_overall?.toFixed(1)}
                                        <span className="text-gray-400">({item.rating_count})</span>
                                      </span>
                                    )}
                                    {item.photo_count > 0 && (
                                      <span className="flex items-center gap-0.5 text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-full">
                                        <Camera className="w-3 h-3" />
                                        {item.photo_count}
                                      </span>
                                    )}
                                  </div>
                                  {item.description && <p className="text-sm text-muted-foreground mt-1">{item.description}</p>}
                                  <div className="flex flex-wrap gap-1 mt-2">
                                    {item.is_dine_in && <span className="bg-rose-100 text-rose-600 px-2 py-0.5 rounded-full text-xs">Sur place</span>}
                                    {item.is_takeaway && <span className="bg-emerald-100 text-emerald-600 px-2 py-0.5 rounded-full text-xs">A emporter</span>}
                                    {(item.tags || []).map(t => (
                                      <span key={t} className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs">{t}</span>
                                    ))}
                                  </div>
                                  {/* Rating bars */}
                                  {item.avg_overall > 0 && (
                                    <div className="flex items-center gap-4 mt-2 text-[10px] text-muted-foreground">
                                      <span>Goût {item.avg_taste?.toFixed(1)}</span>
                                      <span>Présentation {item.avg_presentation?.toFixed(1)}</span>
                                      <span>Rapport Q/P {item.avg_value_for_money?.toFixed(1)}</span>
                                    </div>
                                  )}
                                </div>
                                <div className="flex flex-col items-end gap-2 ml-3">
                                  <p className="text-lg font-bold">{item.price.toFixed(2)}€</p>
                                  <div className="flex gap-1">
                                    <Button
                                      size="sm" variant="ghost" className="h-7 w-7 p-0 text-blue-500 hover:bg-blue-50"
                                      onClick={() => { setPhotoTargetItem(item); setShowPhotoUpload(true); }}
                                      title="Poster une photo"
                                      data-testid={`photo-btn-${item.id}`}
                                    >
                                      <Camera className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      size="sm" variant="ghost" className="h-7 w-7 p-0 text-amber-500 hover:bg-amber-50"
                                      onClick={() => { setRatingTargetItem(item); setShowDishRating(true); }}
                                      title="Noter ce plat"
                                      data-testid={`rate-btn-${item.id}`}
                                    >
                                      <Star className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </TabsContent>
          )}

          {/* Reviews Tab */}
          {store.business_type === 'restaurant' && (
            <TabsContent value="reviews">
              {/* Review Stats */}
              {reviewStats && (
                <div className="bg-white rounded-xl p-6 mb-6 shadow-sm">
                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <p className="text-4xl font-bold text-amber-500">{reviewStats.avg_overall}</p>
                      <p className="text-xs text-muted-foreground">{reviewStats.count} avis</p>
                    </div>
                    <div className="flex-1 space-y-2">
                      {[
                        { label: 'Cuisine', value: reviewStats.avg_cuisine },
                        { label: 'Service', value: reviewStats.avg_service },
                        { label: 'Ambiance', value: reviewStats.avg_ambiance },
                        { label: 'Qualité/Prix', value: reviewStats.avg_value }
                      ].map(r => (
                        <div key={r.label} className="flex items-center gap-2 text-sm">
                          <span className="w-20 text-muted-foreground">{r.label}</span>
                          <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full bg-amber-400 rounded-full" style={{ width: `${(r.value / 5) * 100}%` }} />
                          </div>
                          <span className="w-8 text-right font-medium">{r.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Add Review Button */}
              <Button className="mb-4" onClick={() => setShowReviewForm(true)} data-testid="add-review-btn">
                ⭐ Laisser un avis
              </Button>

              {/* Reviews List */}
              {reviews.length === 0 ? (
                <div className="bg-white rounded-xl p-12 text-center">
                  <p className="text-4xl mb-3">💬</p>
                  <p className="text-muted-foreground">Aucun avis pour le moment. Soyez le premier !</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {reviews.map(review => (
                    <div key={review.id} className="bg-white rounded-xl p-4 shadow-sm" data-testid={`review-${review.id}`}>
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
                          {review.user_pseudo?.charAt(0)?.toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-sm">{review.user_pseudo}</p>
                          <p className="text-xs text-muted-foreground">{new Date(review.created_at).toLocaleDateString('fr-FR')}</p>
                        </div>
                        <span className="ml-auto bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full text-xs font-medium">
                          ⭐ {review.rating_overall}/5
                        </span>
                      </div>
                      {review.comment && <p className="text-sm text-charcoal">{review.comment}</p>}
                      <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                        <span>Cuisine: {review.rating_cuisine}/5</span>
                        <span>Service: {review.rating_service}/5</span>
                        <span>Ambiance: {review.rating_ambiance}/5</span>
                        <span>Prix: {review.rating_value}/5</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Review Form Dialog */}
              <Dialog open={showReviewForm} onOpenChange={setShowReviewForm}>
                <DialogContent className="sm:max-w-md" data-testid="review-form-dialog">
                  <DialogHeader>
                    <DialogTitle>Laisser un avis</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-2">
                    {[
                      { key: 'rating_cuisine', label: 'Cuisine' },
                      { key: 'rating_service', label: 'Service' },
                      { key: 'rating_ambiance', label: 'Ambiance' },
                      { key: 'rating_value', label: 'Rapport qualité/prix' }
                    ].map(({ key, label }) => (
                      <div key={key} className="flex items-center justify-between">
                        <span className="text-sm font-medium">{label}</span>
                        <div className="flex gap-1">
                          {[1, 2, 3, 4, 5].map(n => (
                            <button
                              key={n}
                              onClick={() => setReviewForm(f => ({ ...f, [key]: n }))}
                              className={`w-8 h-8 rounded-full text-sm font-bold transition-colors ${
                                n <= reviewForm[key] ? 'bg-amber-400 text-white' : 'bg-gray-100 text-gray-400'
                              }`}
                              data-testid={`${key}-${n}`}
                            >
                              {n}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                    <div>
                      <label className="text-sm font-medium">Commentaire (optionnel)</label>
                      <textarea
                        className="w-full mt-1 p-3 border rounded-lg text-sm"
                        rows={3}
                        value={reviewForm.comment}
                        onChange={(e) => setReviewForm(f => ({ ...f, comment: e.target.value }))}
                        placeholder="Décrivez votre expérience..."
                        data-testid="review-comment"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" onClick={() => setShowReviewForm(false)}>Annuler</Button>
                    <Button onClick={submitReview} data-testid="submit-review-btn">Publier l'avis</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </TabsContent>
          )}

          {/* Catalog Tab */}
          <TabsContent value="catalog">
            {/* Filters */}
            <div className="flex flex-wrap gap-2 mb-6">
              <button
                onClick={() => setSelectedCategory('')}
                className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                  selectedCategory === '' ? 'bg-primary text-white' : 'bg-white text-charcoal hover:bg-gray-100'
                }`}
              >
                Tout
              </button>
              {CATEGORIES.filter(cat => products.some(p => p.category === cat.value)).map((cat) => (
                <button
                  key={cat.value}
                  onClick={() => setSelectedCategory(cat.value)}
                  className={`px-3 py-1.5 rounded-full text-sm transition-colors flex items-center gap-1 ${
                    selectedCategory === cat.value ? 'bg-primary text-white' : 'bg-white text-charcoal hover:bg-gray-100'
                  }`}
                >
                  <span>{cat.icon}</span> {cat.label}
                </button>
              ))}
              <button
                onClick={() => setShowPromoOnly(!showPromoOnly)}
                className={`px-3 py-1.5 rounded-full text-sm flex items-center gap-1 transition-colors ${
                  showPromoOnly ? 'bg-red-500 text-white' : 'bg-white text-red-500 hover:bg-red-50'
                }`}
              >
                <Percent className="w-3 h-3" /> Promos
              </button>
            </div>

            {/* Products */}
            {filteredProducts.length === 0 ? (
              <div className="bg-white rounded-xl p-12 text-center">
                <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-muted-foreground">Aucun produit dans cette catégorie</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {filteredProducts.map((product) => {
                  const cartItem = getItemInCart(product.id);
                  return (
                    <div key={product.id} className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                      <div className="aspect-square bg-gray-100 relative">
                        {product.image ? (
                          <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <span className="text-4xl">
                              {CATEGORIES.find(c => c.value === product.category)?.icon || '📦'}
                            </span>
                          </div>
                        )}
                        {product.is_promotion && (
                          <span className="absolute top-2 left-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                            Promo
                          </span>
                        )}
                        {product.is_local && (
                          <span className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                            🌱 Local
                          </span>
                        )}
                      </div>
                      <div className="p-3">
                        <h3 className="font-medium text-charcoal text-sm line-clamp-2">{product.name}</h3>
                        {product.brand && (
                          <p className="text-xs text-muted-foreground">{product.brand}</p>
                        )}
                        <div className="mt-2 flex items-baseline gap-2">
                          {product.is_promotion && product.promotion_price ? (
                            <>
                              <span className="text-lg font-bold text-red-600">{product.promotion_price.toFixed(2)}€</span>
                              <span className="text-sm text-muted-foreground line-through">{product.price.toFixed(2)}€</span>
                            </>
                          ) : (
                            <span className="text-lg font-bold text-charcoal">{product.price.toFixed(2)}€</span>
                          )}
                          <span className="text-xs text-muted-foreground">/ {product.unit}</span>
                        </div>
                        
                        {/* Add to cart button */}
                        <div className="mt-3">
                          {cartItem ? (
                            <div className="flex items-center justify-between bg-primary/10 rounded-lg p-1">
                              <button
                                onClick={() => updateCartItem(product.id, cartItem.quantity - 1)}
                                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-primary/20"
                              >
                                <Minus className="w-4 h-4 text-primary" />
                              </button>
                              <span className="font-medium text-primary">{cartItem.quantity}</span>
                              <button
                                onClick={() => updateCartItem(product.id, cartItem.quantity + 1)}
                                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-primary/20"
                              >
                                <Plus className="w-4 h-4 text-primary" />
                              </button>
                            </div>
                          ) : (
                            <Button 
                              size="sm" 
                              className="w-full"
                              onClick={() => addToCart(product.id)}
                            >
                              <Plus className="w-4 h-4 mr-1" />
                              Ajouter
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>


          {/* Gallery Tab */}
          {store.business_type === 'restaurant' && gallery && gallery.total_photos > 0 && (
            <TabsContent value="gallery">
              <div className="space-y-8">
                {gallery.by_dish.map(dish => (
                  <div key={dish.menu_item_id}>
                    <div className="flex items-center gap-2 mb-3">
                      <h3 className="text-lg font-semibold">{dish.dish_name}</h3>
                      <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full flex items-center gap-1">
                        <Camera className="w-3 h-3" />
                        {dish.photo_count} photo{dish.photo_count > 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                      {dish.photos.map(photo => (
                        <div key={photo.id} className="rounded-xl overflow-hidden bg-gray-100 aspect-square relative group">
                          <img
                            src={photo.photo_base64}
                            alt={photo.caption || dish.dish_name}
                            className="w-full h-full object-cover"
                            data-testid={`gallery-photo-${photo.id}`}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-2">
                            <p className="text-white text-xs font-medium">{photo.user_pseudo}</p>
                            {photo.caption && <p className="text-white/80 text-[10px]">{photo.caption}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>
          )}

          {/* About Tab */}
          <TabsContent value="about">
            <div className="grid md:grid-cols-2 gap-6">
              {store.opening_hours && (
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <h3 className="font-semibold text-charcoal mb-4 flex items-center gap-2">
                    <Clock className="w-5 h-5" /> Horaires d'ouverture
                  </h3>
                  <pre className="text-sm text-muted-foreground whitespace-pre-wrap font-sans">
                    {store.opening_hours}
                  </pre>
                </div>
              )}

              {store.brands?.length > 0 && (
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <h3 className="font-semibold text-charcoal mb-4 flex items-center gap-2">
                    <Tag className="w-5 h-5" /> Marques proposées
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {store.brands.map((brand) => (
                      <span key={brand} className="bg-gray-100 px-3 py-1 rounded-full text-sm">
                        {brand}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {store.local_producers?.length > 0 && (
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <h3 className="font-semibold text-charcoal mb-4 flex items-center gap-2">
                    <Users className="w-5 h-5" /> Producteurs locaux partenaires
                  </h3>
                  <div className="space-y-3">
                    {store.local_producers.map((producer, index) => (
                      <div key={index} className="p-3 bg-green-50 rounded-lg">
                        <p className="font-medium text-charcoal">🌾 {producer.name}</p>
                        {producer.description && (
                          <p className="text-sm text-muted-foreground mt-1">{producer.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {store.gallery_images?.length > 0 && (
                <div className="bg-white rounded-xl p-6 shadow-sm md:col-span-2">
                  <h3 className="font-semibold text-charcoal mb-4">Galerie photos</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {store.gallery_images.map((img, index) => (
                      <div key={index} className="aspect-square rounded-lg overflow-hidden">
                        <img src={img} alt={`Gallery ${index + 1}`} className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Fixed Cart Bar */}
      {cart.items.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-4 z-50">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                <ShoppingCart className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="font-medium text-charcoal">
                  {cart.items.reduce((sum, item) => sum + item.quantity, 0)} article{cart.items.length > 1 ? 's' : ''}
                </p>
                <p className="text-lg font-bold text-primary">{cart.total.toFixed(2)}€</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowCart(true)}>
                Voir le panier
              </Button>
              <Button onClick={() => { setShowCart(true); }}>
                Commander
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Cart Dialog */}
      <Dialog open={showCart} onOpenChange={setShowCart}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5" />
              Mon panier
            </DialogTitle>
          </DialogHeader>

          {!showCheckout ? (
            <>
              {/* Cart Items */}
              <div className="space-y-3">
                {cart.items.map((item) => (
                  <div key={item.product_id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    {item.image ? (
                      <img src={item.image} alt={item.name} className="w-16 h-16 rounded-lg object-cover" />
                    ) : (
                      <div className="w-16 h-16 rounded-lg bg-gray-200 flex items-center justify-center text-2xl">
                        📦
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="font-medium text-charcoal text-sm">{item.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {item.price.toFixed(2)}€ / {item.unit}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => updateCartItem(item.product_id, item.quantity - 1)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-200 hover:bg-gray-300"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="w-8 text-center font-medium">{item.quantity}</span>
                      <button
                        onClick={() => updateCartItem(item.product_id, item.quantity + 1)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-200 hover:bg-gray-300"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="font-bold text-charcoal w-20 text-right">
                      {item.total.toFixed(2)}€
                    </p>
                  </div>
                ))}
              </div>

              {/* Total */}
              <div className="border-t pt-4 mt-4">
                <div className="flex justify-between text-lg font-bold">
                  <span>Total</span>
                  <span className="text-primary">{cart.total.toFixed(2)}€</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 mt-4">
                <Button variant="outline" onClick={clearCart} className="text-red-500">
                  <Trash2 className="w-4 h-4 mr-1" />
                  Vider
                </Button>
                <Button className="flex-1" onClick={() => setShowCheckout(true)}>
                  Choisir un créneau
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* Checkout Form */}
              <div className="space-y-4">
                {/* Date Selection */}
                <div>
                  <label className="text-sm font-medium mb-2 block">Date de retrait *</label>
                  <div className="grid grid-cols-3 gap-2">
                    {getAvailableDates().map((date) => (
                      <button
                        key={date.value}
                        onClick={() => handleDateChange(date.value)}
                        className={`p-2 rounded-lg text-sm text-center transition-colors ${
                          selectedDate === date.value
                            ? 'bg-primary text-white'
                            : 'bg-gray-100 hover:bg-gray-200 text-charcoal'
                        }`}
                      >
                        {date.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Time Slot Selection */}
                {selectedDate && (
                  <div>
                    <label className="text-sm font-medium mb-2 block">Créneau horaire *</label>
                    {availableSlots.length === 0 ? (
                      <p className="text-sm text-muted-foreground bg-gray-50 p-3 rounded-lg">
                        Aucun créneau disponible pour cette date
                      </p>
                    ) : (
                      <div className="grid grid-cols-2 gap-2">
                        {availableSlots.map((slot) => (
                          <button
                            key={slot.id}
                            onClick={() => setSelectedSlot(slot)}
                            className={`p-3 rounded-lg text-sm text-center transition-colors ${
                              selectedSlot?.id === slot.id
                                ? 'bg-primary text-white'
                                : 'bg-gray-100 hover:bg-gray-200 text-charcoal'
                            }`}
                          >
                            <Clock className="w-4 h-4 mx-auto mb-1" />
                            {slot.start_time} - {slot.end_time}
                            <p className="text-xs opacity-70">{slot.remaining_spots} place{slot.remaining_spots > 1 ? 's' : ''}</p>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Customer Info */}
                <div>
                  <label className="text-sm font-medium mb-1 block">Votre nom *</label>
                  <Input
                    value={checkoutData.customer_name}
                    onChange={(e) => setCheckoutData({ ...checkoutData, customer_name: e.target.value })}
                    placeholder="Jean Dupont"
                    required
                  />
                </div>

                <div>
                  <label className="text-sm font-medium mb-1 block">Téléphone *</label>
                  <Input
                    value={checkoutData.customer_phone}
                    onChange={(e) => setCheckoutData({ ...checkoutData, customer_phone: e.target.value })}
                    placeholder="06 12 34 56 78"
                    required
                  />
                </div>

                <div>
                  <label className="text-sm font-medium mb-1 block">Email (optionnel)</label>
                  <Input
                    type="email"
                    value={checkoutData.customer_email}
                    onChange={(e) => setCheckoutData({ ...checkoutData, customer_email: e.target.value })}
                    placeholder="jean@email.com"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium mb-1 block">Notes (optionnel)</label>
                  <textarea
                    value={checkoutData.notes}
                    onChange={(e) => setCheckoutData({ ...checkoutData, notes: e.target.value })}
                    placeholder="Instructions spéciales..."
                    className="w-full p-3 border rounded-lg resize-none h-20"
                  />
                </div>

                {/* Summary */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-charcoal mb-2">Récapitulatif</h4>
                  <p className="text-sm text-muted-foreground">
                    {cart.items.reduce((sum, item) => sum + item.quantity, 0)} article{cart.items.length > 1 ? 's' : ''} • 
                    Retrait {selectedDate && selectedSlot ? `le ${new Date(selectedDate).toLocaleDateString('fr-FR')} de ${selectedSlot.start_time} à ${selectedSlot.end_time}` : '...'}
                  </p>
                  <p className="text-lg font-bold text-primary mt-2">{cart.total.toFixed(2)}€</p>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowCheckout(false)}>
                    Retour
                  </Button>
                  <Button 
                    className="flex-1" 
                    onClick={submitOrder}
                    disabled={!selectedSlot || !checkoutData.customer_name || !checkoutData.customer_phone || submitting}
                  >
                    {submitting ? 'Commande en cours...' : 'Confirmer la commande'}
                    <Check className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Photo Upload Dialog */}
      <Dialog open={showPhotoUpload} onOpenChange={setShowPhotoUpload}>
        <DialogContent className="sm:max-w-md" data-testid="photo-upload-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-blue-500" />
              Photo de {photoTargetItem?.name}
            </DialogTitle>
            <DialogDescription>Prenez une photo de votre plat et gagnez +5 points !</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="block text-sm font-medium mb-2">Choisir une photo</label>
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handlePhotoUpload}
                className="w-full text-sm file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-600 hover:file:bg-blue-100"
                data-testid="photo-file-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Légende (optionnel)</label>
              <Input
                value={photoCaption}
                onChange={(e) => setPhotoCaption(e.target.value)}
                placeholder="Un délicieux plat..."
                data-testid="photo-caption-input"
              />
            </div>
            {uploadingPhoto && (
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground py-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Envoi en cours...
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Votre photo sera soumise à validation par le restaurant. Si elle est approuvée, vous gagnerez +20 points supplémentaires !
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dish Rating Dialog */}
      <Dialog open={showDishRating} onOpenChange={setShowDishRating}>
        <DialogContent className="sm:max-w-md" data-testid="dish-rating-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Star className="w-5 h-5 text-amber-500" />
              Noter : {ratingTargetItem?.name}
            </DialogTitle>
            <DialogDescription>Votre avis détaillé aide la communauté (+3 points)</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <StarRating label="Goût" value={dishRatingForm.taste} onChange={(v) => setDishRatingForm(p => ({...p, taste: v}))} />
            <StarRating label="Présentation" value={dishRatingForm.presentation} onChange={(v) => setDishRatingForm(p => ({...p, presentation: v}))} />
            <StarRating label="Rapport qualité/prix" value={dishRatingForm.value_for_money} onChange={(v) => setDishRatingForm(p => ({...p, value_for_money: v}))} />
            <div>
              <label className="block text-sm font-medium mb-1">Commentaire (optionnel)</label>
              <Input
                value={dishRatingForm.comment}
                onChange={(e) => setDishRatingForm(p => ({...p, comment: e.target.value}))}
                placeholder="Excellent plat, bien assaisonné..."
                data-testid="dish-rating-comment"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowDishRating(false)}>Annuler</Button>
              <Button onClick={submitDishRating} className="bg-amber-500 hover:bg-amber-600" data-testid="submit-dish-rating-btn">
                <Star className="w-4 h-4 mr-1" /> Publier ma note
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
